import { HttpClient } from '@angular/common/http';
import { computed, DestroyRef, inject, Injectable, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { BackendService } from '@ipg/ngx-frontend-toolset';
import { AudioPlayerService } from './audio-player.service';
import { VarService } from './var.service';

@Injectable({
  providedIn: 'root',
})
export class TtsPlayerService {
  private readonly _http = inject(HttpClient);
  private readonly _backend = inject(BackendService);
  private readonly _audioPlayer = inject(AudioPlayerService);
  private readonly _varService = inject(VarService);
  private readonly _destroyRef = inject(DestroyRef);

  private readonly _currentTtsId = signal<string | null>(null);
  private _currentBlobUrl: string | null = null;
  private readonly _cache = new Map<string, Blob>();

  readonly currentTtsId = this._currentTtsId.asReadonly();

  readonly isPlaying = computed(
    () =>
      this._audioPlayer.state().playing &&
      this._currentTtsId() !== null &&
      this._audioPlayer.currentTrack()?.id === this._currentTtsId()
  );

  play(text: string, id: string, voice?: string, lang?: string): void {
    // If same TTS is playing, stop it
    if (this.isPlaying() && this._currentTtsId() === id) {
      this.stop();
      return;
    }

    // Stop any current playback (audio actions or other TTS)
    this._audioPlayer.stop();
    this._revokeBlobUrl();
    this._currentTtsId.set(id);

    const resolvedText = this._resolveVariables(text);
    const cacheKey = [lang, voice, resolvedText].filter(Boolean).join('::');
    const cached = this._cache.get(cacheKey);

    if (cached) {
      this._playBlob(cached, id, text);
      return;
    }

    let previewUrl = `/tts/preview?text=${encodeURIComponent(resolvedText)}`;
    if (voice) {
      previewUrl += `&voice=${encodeURIComponent(voice)}`;
    }
    if (lang) {
      previewUrl += `&lang=${encodeURIComponent(lang)}`;
    }
    const url = this._backend.buildUrl(previewUrl);

    this._http
      .get(url, { responseType: 'blob' })
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: (blob) => {
          this._cache.set(cacheKey, blob);
          this._playBlob(blob, id, text);
        },
        error: () => {
          this._currentTtsId.set(null);
          this._revokeBlobUrl();
        },
      });
  }

  stop(): void {
    this._audioPlayer.stop();
    this._currentTtsId.set(null);
    this._revokeBlobUrl();
  }

  clearCache(): void {
    this._cache.clear();
  }

  private _playBlob(blob: Blob, id: string, title: string): void {
    this._currentBlobUrl = URL.createObjectURL(blob);
    this._audioPlayer.load({ id, title, src: this._currentBlobUrl });
    this._audioPlayer.play();
  }

  private _resolveVariables(text: string): string {
    const varMap = this._varService.varMap();
    return text.replace(/\[([^\]]+)\]/g, (match, key: string) => {
      const varInfo = varMap.get(key);
      return varInfo ? varInfo.value : match;
    });
  }

  private _revokeBlobUrl(): void {
    if (this._currentBlobUrl) {
      URL.revokeObjectURL(this._currentBlobUrl);
      this._currentBlobUrl = null;
    }
  }
}
